import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Info } from 'lucide-react';

const PasswordChangeSection = ({ profile }) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [isOAuthUser, setIsOAuthUser] = useState(false);

  // Detect if user signed up via OAuth (Google/Facebook) — they have no password
  useEffect(() => {
    const checkAuthProvider = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const provider = session.user.app_metadata?.provider;
          // OAuth providers don't set a password during signup
          setIsOAuthUser(provider === 'google' || provider === 'facebook');
        }
      } catch (e) {
        // Default to requiring current password if detection fails
      }
    };
    checkAuthProvider();
  }, []);

  const handleForgotPassword = async () => {
    if (!profile?.email) return;
    setSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(profile.email, {
        redirectTo: `${window.location.origin}/callback?flow=recovery`
      });
      if (error) throw error;
      toast.success(t('profile.resetLinkSent', 'Password reset link sent to your email!'));
    } catch (error) {
      toast.error(error.message || 'Failed to send reset email');
    } finally {
      setSendingReset(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validation
    if (!isOAuthUser && !currentPassword) {
      toast.error('Please enter your current password');
      return;
    }

    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (!isOAuthUser && currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      // For email/password users, verify current password first
      if (!isOAuthUser) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: profile?.email,
          password: currentPassword
        });

        if (signInError) {
          toast.error('Current password is incorrect');
          setLoading(false);
          return;
        }
      }

      // Update password (works for all auth types when user is authenticated)
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success(isOAuthUser
        ? 'Password set successfully! You can now also log in with email and password.'
        : 'Password updated successfully!'
      );
      // Clear all fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(error.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="password-change-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock size={20} />
          {isOAuthUser
            ? t('profile.setPassword', 'Set Password')
            : t('profile.changePassword')}
        </CardTitle>
        <CardDescription>
          {isOAuthUser
            ? t('profile.setPasswordDesc', 'Set a password so you can also log in with your email and password.')
            : t('profile.updatePasswordDesc')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          {/* OAuth users: show info banner instead of current password field */}
          {isOAuthUser ? (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
              <Info size={18} className="mt-0.5 shrink-0" />
              <p>
                {t('profile.oauthPasswordInfo',
                  "You signed up with Google or Facebook, so you don't have a password yet. Set one below to also be able to log in with your email and password."
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="current-password">{t('profile.currentPassword')}</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrent ? "text" : "password"}
                  placeholder={t('profile.enterCurrentPassword')}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={sendingReset}
                className="text-xs text-primary hover:underline disabled:opacity-50"
              >
                {sendingReset
                  ? t('profile.sendingResetLink', 'Sending reset link...')
                  : t('profile.forgotCurrentPassword', "Forgot your current password?")}
              </button>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="new-password">
              {isOAuthUser
                ? t('profile.password', 'Password')
                : t('profile.newPassword')}
            </Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNew ? "text" : "password"}
                placeholder={isOAuthUser
                  ? t('profile.enterPassword', 'Enter a password')
                  : t('profile.enterNewPassword')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t('profile.confirmNewPassword')}</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirm ? "text" : "password"}
                placeholder={t('profile.confirmNewPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading || (!isOAuthUser && !currentPassword) || !newPassword || !confirmPassword}
            data-testid="change-password-button"
          >
            {loading
              ? t('profile.saving')
              : isOAuthUser
                ? t('profile.setPasswordBtn', 'Set Password')
                : t('profile.updatePassword')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default PasswordChangeSection;
